
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, Minus, Plus } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  questionsAPI,
  questionBankAPI,
  questionTypesAPI,
  categoriesAPI,
} from "../../services/api";
import QuestionsTable from "./questions/QuestionsTable";
import AddEditQuestionModal from "./questions/AddEditQuestionModal";
import BulkOperationsModal from "./questions/BulkOperationsModal";
import QuestionExcelUpload from "./questions/QuestionExcelUpload";

const Questions = () => {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Get question bank info from navigation state
  const questionBankFromState = location.state || {};
  const questionBankId = questionBankFromState._id || null;
  const selectedQuestionBankName = questionBankFromState.name || "";

  const [questions, setQuestions] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [questionBanks, setQuestionBanks] = useState([]);
  const [currentQuestionBank, setCurrentQuestionBank] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bulk operations state
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkModalMode, setBulkModalMode] = useState("edit"); // 'edit' or 'add'

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchQuestions();
    fetchQuestionTypes();
    fetchCategories();
    fetchQuestionBanks();
    if (questionBankId) {
      fetchCurrentQuestionBank();
    }
  }, [currentPage, pageSize, searchTerm, selectedDifficulty, questionBankId]);

  const fetchCurrentQuestionBank = async () => {
    try {
      const response = await questionBankAPI.getOne(questionBankId);
      setCurrentQuestionBank(response.data);
    } catch (error) {
      console.error("Failed to fetch current question bank:", error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedDifficulty !== "all" && {
          difficultyLevel: selectedDifficulty,
        }),
        ...(questionBankId && { questionBankId: questionBankId }),
      };

      const response = await questionsAPI.getAll(params);
      setQuestions(response.data.questions);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      toast({
        title: "Error",
        description: "Failed to fetch questions",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if question bank has space for new questions
  const checkQuestionBankCapacity = (questionsToAdd = 1) => {
    if (!currentQuestionBank) return true;
    
    const currentCount = currentQuestionBank.questionCount || 0;
    const maxCapacity = currentQuestionBank.maxCapacity || 5000;
    const availableSpace = maxCapacity - currentCount;
    
    return availableSpace >= questionsToAdd;
  };

  // ... keep existing code (fetchQuestionTypes, fetchCategories, fetchQuestionBanks functions remain the same)
  const fetchQuestionTypes = async () => {
    try {
      const response = await questionTypesAPI.getAll();
      setQuestionTypes(response.data);
    } catch (error) {
      console.error("Failed to fetch question types:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      // Destructure the arrays
      const { userCategories, companyCategories } = response.data;

      // Combine both arrays and map their names
      const allCategories = [
        ...userCategories.map((cat) => cat.name),
        ...companyCategories.map((cat) => cat.name),
      ];

      setCategories(allCategories);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchQuestionBanks = async () => {
    try {
      const response = await questionBankAPI.getAll();
      setQuestionBanks(response.data);
    } catch (error) {
      console.error("Failed to fetch question banks:", error);
    }
  };

  // Handle add question with capacity check
  const handleAddQuestionClick = () => {
    if (!checkQuestionBankCapacity(1)) {
      toast({
        title: "Limit Reached",
        description: "Question bank has reached its maximum capacity. Cannot add more questions.",
        variant: "destructive",
      });
      return;
    }
    setShowAddModal(true);
  };

  // ... keep existing code (handleAddQuestion, handleEditQuestion functions)
  const handleAddQuestion = async (questionData) => {
    try {
      setIsSubmitting(true);
      await questionsAPI.create(questionData);

      toast({
        title: "Success",
        description: "Question created successfully",
      });

      setShowAddModal(false);
      fetchQuestions();
      fetchCurrentQuestionBank(); // Refresh question bank data
    } catch (error) {
      console.error("Failed to create question:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to create question",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditQuestion = async (questionData) => {
    try {
      setIsSubmitting(true);
      await questionsAPI.update(editingQuestion._id, questionData);

      toast({
        title: "Success",
        description: "Question updated successfully",
      });

      setEditingQuestion(null);
      fetchQuestions();
      fetchCurrentQuestionBank(); // Refresh question bank data
    } catch (error) {
      console.error("Failed to update question:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update question",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }

    try {
      await questionsAPI.delete(questionId);

      toast({
        title: "Success",
        description: "Question deleted successfully",
      });

      fetchQuestions();
      fetchCurrentQuestionBank(); // Refresh question bank data
    } catch (error) {
      console.error("Failed to delete question:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete question",
        variant: "destructive",
      });
    }
  };

  // Bulk operations handlers
  const handleBulkDelete = async (questionIds) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${questionIds.length} question(s)?`
      )
    ) {
      return;
    }

    try {
      await questionsAPI.bulkDelete(questionIds);
      toast({
        title: "Success",
        description: `${questionIds.length} question(s) deleted successfully`,
      });
      setSelectedQuestions([]);
      fetchQuestions();
      fetchCurrentQuestionBank(); // Refresh question bank data
    } catch (error) {
      console.error("Failed to delete questions:", error);
      toast({
        title: "Error",
        description: "Failed to delete questions",
        variant: "destructive",
      });
    }
  };

  const handleBulkEdit = () => {
    const questionsToEdit = questions.filter((q) =>
      selectedQuestions.includes(q._id)
    );
    setBulkModalMode("edit");
    setShowBulkModal(true);
  };

  const handleBulkAdd = () => {
    // Check if bulk modal is already open
    if (showBulkModal) {
      toast({
        title: "Action Not Allowed",
        description: "Please close the current bulk operation before starting a new one.",
        variant: "destructive",
      });
      return;
    }

    setBulkModalMode("add");
    setShowBulkModal(true);
  };

 const handleBulkSubmit = async (bulkQuestions, isPartial = false) => {
    try {
      setIsSubmitting(true);

      if (bulkModalMode === "edit") {
        await questionsAPI.bulkUpdate(bulkQuestions);
        toast({
          title: "Success",
          description: `${bulkQuestions.length} question(s) updated successfully`,
        });
      } else {
        // Handle batch submission for large uploads
        const response = await questionBankAPI.submitValidated({
          validatedQuestions: bulkQuestions,
          selectedQuestionBank: currentQuestionBank || { _id: questionBankId },
          isPartial: isPartial
        });
        
        if (!isPartial) {
          toast({
            title: "Success",
            description: `${bulkQuestions.length} question(s) created successfully`,
          });
        }
      }

      if (!isPartial) {
        setShowBulkModal(false);
        setSelectedQuestions([]);
        fetchQuestions();
        fetchCurrentQuestionBank(); // Refresh question bank data
      }
    } catch (error) {
      console.error("Failed to process bulk operation:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to process bulk operation",
        variant: "destructive",
      });
    } finally {
      if (!isPartial) {
        setIsSubmitting(false);
      }
    }
  };

  const handleDownloadSelected = async () => {
    try {
      await questionsAPI.downloadSelected(selectedQuestions);
      toast({
        title: "Success",
        description: "Selected questions downloaded successfully",
      });
    } catch (error) {
      console.error("Failed to download selected questions:", error);
      toast({
        title: "Error",
        description: "Failed to download selected questions",
        variant: "destructive",
      });
    }
  };

  const handleDownloadAll = async () => {
    try {
      await questionsAPI.downloadAll(questionBankId);
      toast({
        title: "Success",
        description: "All questions downloaded successfully",
      });
    } catch (error) {
      console.error("Failed to download all questions:", error);
      toast({
        title: "Error",
        description: "Failed to download all questions",
        variant: "destructive",
      });
    }
  };

  // ... keep existing code (handleUploadSuccess, showToast functions)
  const handleUploadSuccess = () => {
    fetchQuestions();
    fetchCurrentQuestionBank(); // Refresh question bank data
    toast({
      title: "Success",
      description: "Questions uploaded successfully",
    });
  };

  const showToast = (type, title, description) => {
    toast({
      title,
      description,
      variant: type === "error" ? "destructive" : "default",
    });
  };

  // Get capacity info for display
  const getCapacityInfo = () => {
    if (!currentQuestionBank) return null;
    
    const currentCount = currentQuestionBank.questionCount || 0;
    const maxCapacity = currentQuestionBank.maxCapacity || 5000;
    const availableSpace = maxCapacity - currentCount;
    const percentage = Math.round((currentCount / maxCapacity) * 100);
    
    return {
      current: currentCount,
      max: maxCapacity,
      available: availableSpace,
      percentage
    };
  };

  const capacityInfo = getCapacityInfo();

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Questions - {selectedQuestionBankName}
          </h1>
          <p className="text-muted-foreground">
            Manage your question library
            {questionBankId && (
              <span className="ml-2 text-blue-600">
                (Filtered by Question Bank)
              </span>
            )}
          </p>
          {capacityInfo && (
            <div className="mt-2 text-sm text-gray-600">
              Capacity: {capacityInfo.current}/{capacityInfo.max} questions ({capacityInfo.percentage}% used)
              {capacityInfo.available === 0 && (
                <span className="ml-2 text-red-600 font-medium">• LIMIT REACHED</span>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <QuestionExcelUpload
            dropDownQuestionBank={questionBanks}
            onUploadSuccess={handleUploadSuccess}
            showToast={showToast}
            selectedQuestionBankId={questionBankId}
          />

          <Button onClick={handleAddQuestionClick}>
            <Plus className="mr-2 h-4 w-4" />
            Add Question
          </Button>
          <Button
            onClick={() => navigate("/admin/question_bank")}
            className="bg-black text-white hover:bg-gray-800"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Questions ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          <QuestionsTable
            questions={questions}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedDifficulty={selectedDifficulty}
            setSelectedDifficulty={setSelectedDifficulty}
            onEdit={setEditingQuestion}
            onDelete={handleDeleteQuestion}
            isLoading={isLoading}
            selectedQuestions={selectedQuestions}
            setSelectedQuestions={setSelectedQuestions}
            onBulkDelete={handleBulkDelete}
            onBulkEdit={handleBulkEdit}
            onBulkAdd={handleBulkAdd}
            onDownloadSelected={handleDownloadSelected}
            onDownloadAll={handleDownloadAll}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />

          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddEditQuestionModal
        open={showAddModal || !!editingQuestion}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddModal(false);
            setEditingQuestion(null);
          }
        }}
        question={editingQuestion}
        onSubmit={editingQuestion ? handleEditQuestion : handleAddQuestion}
        questionTypes={questionTypes}
        categories={categories}
        questionBanks={questionBanks}
        isLoading={isSubmitting}
        selectedQuestionBankId={questionBankId}
      />

      <BulkOperationsModal
        open={showBulkModal}
        onOpenChange={setShowBulkModal}
        mode={bulkModalMode}
        selectedQuestions={
          bulkModalMode === "edit"
            ? questions.filter((q) => selectedQuestions.includes(q._id))
            : []
        }
        onSubmit={handleBulkSubmit}
        questionTypes={questionTypes}
        categories={categories}
        questionBanks={questionBanks}
        isLoading={isSubmitting}
        selectedQuestionBankId={questionBankId}
        currentQuestionBank={currentQuestionBank}
      />
    </div>
  );
};

export default Questions;